export type Medication = {
  name: string;
  dosage: string;
  frequency: string;
  prescriber: PractitionerInfo | null;
  date: string;
  time: string;
  status: string;
  refill: number;
  reasonCode: string;
};

export type Identifier = {
  name: string;
  objectIdentifier: string;
  value: string;
};

export type Patient = {
  id: string;
  name: string;
  age: number;
  gender: string;
  primaryIdentifier: Identifier;
  dateOfBirth: string;
  phone: string;
  email: string;
  emergencyContact: string;
  address: string;
  primaryPhysician: string;
  lastName: string;
  givenNames: string[];
};


export type NameDetails = {
  use?: string;
  text?: string;
  family?: string;
  given?: string[];
  prefix?: unknown[];
  suffix?: unknown[];
};

export type Qualification = {
  text?: string;
  code?: string;
  period?: { start?: string; end?: string };
};

export type PractitionerInfo = {
  practitioner_id: string;
  name: string;
  nameDetails?: NameDetails | null;
  identifiers?: Identifier[];
  active?: boolean;
  gender?: string;
  photo?: string;
  location?: string;
  locationPhone?: string;
  practitionerEmail?: string;
  specialty?: string[];
  telecom?: Array<{ system?: string; value?: string; use?: string }>;
  qualification?: Qualification[] | null;
};

export type Encounter = {
  id: string;
  type: string;
  date: string;
  status: string;
  practitioner: PractitionerInfo | null;
  reasonCode: string;
  csn: string;
};

export type VitalSign = {
  name: string;
  value: string;
  unit: string;
  date: string;
  time: string;
  status: string;
  isAbnormal: boolean;
  referenceRange: string;
  note: string;
  effectiveDateTime: Date | null;
};

export type LabResult = {
  name: string;
  value: string;
  unit: string;
  date: string;
  status: string;
  time: string;
  isAbnormal: boolean;
  referenceRange: string;
  labCategory: string;
  note: string;
};

export type Immunization = {
  name: string;
  date: string;
  time: string | null;
  status: string;
  lotNumber: string;
  manufacturer: string;
  reasonCode: string;
};

export type Procedures = {
  id: string;
  identifiers: Identifier[] | null;
  name: string;
  date: string;
  startTime: string | null;
  endTime: string | null;
  status: string;
  location: string;
  recorder: PractitionerInfo | null;
  reasonCode: string;
  encounter: Encounter | null;
};

export type ServiceRequest = {
  id: string;
  name: string;
  date: string;
  time: string;
  status: string;
  requestor: PractitionerInfo | null;
  category: string;
  reasonCode: string;
};

export type Appointment = {
  date: string;
  startTime: string;
  endTime: string;
  name: string;
  status: string;
  practitioner: PractitionerInfo | null;
  location: string;
  isSurgery: boolean;
  note: string;
  reasonCode: string;
};

export type Document = {
  name: string;
  date: string;
  status: string;
  attachment: {
    url: string;
    contentType: string;
    content: string;
    title: string;
    fileName: string;
  };
};

export type Hl7Log = {
  id: string;
  organization_id: number;
  patient_id: string;
  call_type: string;
  agent_email: string | null;
  outgoing_message: string;
  raw_ack: string;
  ack_http_status: number | null;
  ack_code: string | null;
  ack_accepted: boolean | null;
  ack_control_id: string | null;
  ack_text: string | null;
  ack_err_segment: string | null;
  created_at: string;
  updated_at: string;
};

export type ClinicalNote = {
  id: string;
  name: string;
  date: string;
  time: string;
  status: string;
  author: PractitionerInfo | null;
  contentType: string;
  url: string;
};

export type Practitioner = {
  id: string;
  name: string;
  location: string;
};

export type Slot = {
  id: string;
  start: string;
  end: string;
  serviceType: {
    text?: string;
    coding: {
      system: string;
      code: string;
      display: string;
    }[];
  };
}

export type Location = {
  id: string;
  phone: string;
  name: string;
  address: {
    text: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
};

export type Pagination = {
  nextLink: string | null;
};

export type SlotType = {
  value: string;
  label: string;
  ehrProvider: string;
  isSnomed?: boolean; // Only used for Cerner slot types
};

export type DashboardCard = {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
};

export type User = {
  id: number;
  email: string;
  master_admin: boolean;
  email_verified_at?: Date | null;
  created_at?: Date;
  updated_at?: Date;
}

export type EhrProvider = 'epic' | 'cerner';
export type OAuthProvider = 'epic' | 'google' | 'microsoft' | 'webex' | 'zoom';
export type SettingsMap = Map<string, string>;

export type Organization = {
  id: number;
  name: string;
  ehr_provider: EhrProvider;
  user_role: string;
}

export type OrgMembership = {
  id: number;
  user_id: number;
  organization_id: number;
  role: string;
}

export type AuthState = {
  user: User | null;
  organizations: Organization[];
  isAuthenticated: boolean;
  isLoading: boolean;
  isDemoSession: boolean;
  orgMemberships: OrgMembership[];
}

export type Coverage = {
  id: string;
  status: string;
  subscriberId: string;
  beneficiaryReference: string; // _include patient?
  period: {
    start: string;
    end: string | null;
  };
  payors: {
    reference: string;
    display: string;
  }[];
  classes: {
    value: string;
    name: string;
  }[];
}

export type ExplanationOfBenefit = {
  id: string;
  status: string;
  use: string;
  patientId: string;
  billablePeriod: {
    start: string;
    end: string;
  };
  created: {
    date: string;
    time: string;
  };
  insurer: {
    identifier: {
      use: string;
      system: string;
      value: string;
    };
    display: string;
  };
  provider: {
    reference: string;
    display: string;
  };
  payee: {
    reference: string;
    display: string;
  };
  facility: string;
  outcome: string;
  disposition: string;
  insurance: Coverage[];
  payment: {
    amount: {
      value: number;
      currency: string;
    };
    display: string;
  }[];
  items: {
    productName: string;
    value: number;
    currency: string;
    quantity: number;
    servicedDate: string;
    servicedTime: string;
  }[];
  netTotal: {
    productName: string;
    value: number;
    currency: string;
  }[];
}

export type Billing = {
  id: number;
  organization_id: number;
  subscription_id: string;
  customer_id: string;
  status: string;
  cancel_at: number | null;
  latest_invoice_id: string | null;
  customer_email: string | null;
  subscription_creation_date: number;
  current_billing_period_start: number;
  current_billing_period_end: number;
  subscription_interval: string;
  subscription_product_id: string | null;
}

export type PaginatedResponse<T> = {
  items: T[];
  total: number;
  pageCount: number;
  hasNextPage: boolean;
  currentPage: number;
  pageSize: number;
}

export type PaginationParams = {
  page: number;
  pageSize: number;
  sortProperty: string;
  sortDirection: 'asc' | 'desc';
}

export type ApiKey = {
  id: string;
  name: string;
  created_at: Date;
  last_used_at: Date | null;
  user_email: string;
}

export type PractitionerDirectory = {
  practitioner_role_id?: string;
  practitioner_id: string;
  name: string;
  location: string;
  location_id?: string;
  specialty: string[];
  practitionerEmail?: string;
  locationPhone: string;
  lat?: number;
  lng?: number;
}

export type GeoLocation = {
  lat: number;
  lng: number;
}

export type Specialty = {
  id: string;
  name: string;
  created_at: Date;
}

export type DocumentType = {
  id: string;
  txa_id: string;
  label: string;
  created_at: Date;
}

export const COOKIE_KEYS = {
  AUTH_TOKEN: 'patient_dashboard_auth_token',
} as const;

export const COOKIE_OPTIONS = {
  httpOnly: true,
  secure: true,
  sameSite: 'none' as const, // 'strict',
  path: '/',
  maxAge: 24 * 60 * 60 * 1000, // 24 hours
};

export type MatchPatientBody = {
  lastName: string;
  firstName: string;
  gender?: string;
  birthDate: string;
  phone?: string;
  email?: string;
  ssn?: string;
  address?: {
    line: string[];
    city: string;
    district: string;
    state: string;
    postalCode: string;
    country: string;
  };
};

export enum TabKey {
  SUMMARY = 'summary',
  APPOINTMENTS = 'appointments',
  MEDICATIONS = 'medications',
  VITALS = 'vitals',
  ENCOUNTERS = 'encounters',
  NOTES = 'clinical_notes',
  PROCEDURES = 'procedures',
  IMMUNIZATIONS = 'immunizations',
  LAB_RESULTS = 'lab_results',
  COVERAGE = 'coverage',
}

export enum AuthError {
  OAUTH_FAILED = 'oauth_failed',
  NO_EMAIL = 'no_email',
  OAUTH_ERROR = 'oauth_error',
  USER_NOT_FOUND = 'user_not_found',
  OAUTH_NOT_CONFIGURED = 'oauth_not_configured',
  SESSION_EXPIRED = 'session_expired',
  INCORRECT_PERMISSIONS = 'incorrect_permissions',
}
